import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format currency values - rounds to nearest cent (2 decimal places)
 */
export function formatCurrency(value?: number | null): string {
  if (value == null) return "N/A";

  // Round to nearest cent (2 decimal places)
  const roundedValue = Math.round(value * 100) / 100;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(roundedValue);
}

/**
 * Format area values (square footage)
 */
export function formatArea(value?: number | null, useMetric = false): string {
  if (value == null) return "N/A";

  const formatted = new Intl.NumberFormat("en-US").format(Math.round(value));
  return useMetric ? `${formatted} m²` : `${formatted} SF`;
}

/**
 * Abbreviate number with K, M, B suffix
 */
export function abbreviateNumber(value?: number | null): string {
  if (value == null) return "N/A";

  if (value >= 1000000000) {
    return `${(value / 1000000000).toFixed(1)}B`;
  }

  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }

  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }

  return value.toString();
}

/**
 * Format price for listing display
 * @param price - The price value to format
 * @param showDollarSign - Whether to include dollar sign (default: true)
 * @returns Formatted price string (e.g., "$1,250,000")
 */
export function formatPrice(price: number, showDollarSign: boolean = true): string {
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);

  return showDollarSign ? `$${formatted}` : formatted;
}

/**
 * Format price per square foot
 * @param pricePerSqft - Price per square foot
 * @returns Formatted price/SF string (e.g., "$250/SF")
 */
export function formatPricePerSqft(pricePerSqft: number): string {
  return `$${pricePerSqft.toLocaleString()}/SF`;
}

/**
 * Abbreviated price format for compact displays (e.g., $1.2M, $500K)
 * @param price - The price value to format
 * @param showDollarSign - Whether to include dollar sign (default: true)
 * @returns Abbreviated price string
 */
export function formatPriceAbbreviated(price: number, showDollarSign: boolean = true): string {
  let abbreviated = '';
  let suffix = '';

  if (price >= 1000000) {
    abbreviated = (price / 1000000).toFixed(1);
    suffix = 'M';
  } else if (price >= 1000) {
    abbreviated = (price / 1000).toFixed(1);
    suffix = 'K';
  } else {
    abbreviated = price.toString();
  }

  // Remove trailing .0
  abbreviated = abbreviated.endsWith('.0') ? abbreviated.slice(0, -2) : abbreviated;

  return showDollarSign ? `$${abbreviated}${suffix}` : `${abbreviated}${suffix}`;
}
